import { useState, useEffect } from 'react';
import { Stack, Group, Text, Badge, Button, Paper } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Patient, Encounter, Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { PsychomotorDevelopmentListItem } from './PsychomotorDevelopmentListItem';
import { PsychomotorDevelopmentInputModal } from './PsychomotorDevelopmentInputModal';
import { PsychomotorDevelopmentPrevious } from '@/components/inputs/PsychomotorDevelopmentPrevious';
import {
  loadPreviousEncounterPsychomotorDevelopment,
  savePsychomotorDevelopment,
  loadEncounterPsychomotorDevelopment,
  deletePsychomotorDevelopment,
} from '@/pages/ainigma/patient/examination/examination.helpers';
import type { PsychomotorDevelopmentItem } from '@/types/psychomotor-development.types';
import { notifications } from '@mantine/notifications';
import { ListContentAction } from '../../medical-history/utils/medical-history.types';

export interface PsychomotorDevelopmentListProps {
  items: PsychomotorDevelopmentItem[];
  onChange: (items: PsychomotorDevelopmentItem[]) => void;
  patient: Patient;
  encounter: Encounter;
  readonly?: boolean;
}

export const PsychomotorDevelopmentList = ({
  items,
  onChange,
  patient,
  encounter,
  readonly = false,
}: PsychomotorDevelopmentListProps) => {
  const { t } = useTranslation(['patient-pages']);
  const medplum = useMedplum();
  const profile = useMedplumProfile();

  // Modal states
  const [modalOpened, setModalOpened] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');

  // Previous encounter data
  const [previousData, setPreviousData] = useState<{
    items: PsychomotorDevelopmentItem[];
    encounterDate?: string;
  } | null>(null);
  const [loadingPrevious, setLoadingPrevious] = useState(false);

  // Check if we have any data
  const hasData = items.some(
    (item) => item.value !== null || item.observations
  );

  // Load previous encounter data
  useEffect(() => {
    if (!patient?.id || !encounter?.id) {
      setPreviousData(null);
      return;
    }

    async function loadPrevious() {
      setLoadingPrevious(true);
      try {
        const result = await loadPreviousEncounterPsychomotorDevelopment(
          medplum,
          patient as Patient,
          encounter as Encounter
        );
        setPreviousData(result);
      } catch (error) {
        console.error('Error loading previous psychomotor development:', error);
        setPreviousData(null);
      } finally {
        setLoadingPrevious(false);
      }
    }

    loadPrevious();
  }, [medplum, patient, encounter]);

  // Handlers
  const handleAdd = () => {
    setModalMode('add');
    setModalOpened(true);
  };

  const handleAction = async (action: ListContentAction) => {
    if (action === 'edit') {
      setModalMode('edit');
      setModalOpened(true);
      return;
    }

    if (action === 'remove') {
      try {
        // Delete from server immediately
        await deletePsychomotorDevelopment(medplum, patient, encounter);

        // Update local state
        onChange([]);

        // Show success notification
        notifications.show({
          color: 'green',
          title: t('psychomotorDevelopment.deleteSuccess', {
            ns: 'patient-pages',
            defaultValue: 'Deleted',
          }),
          message: t('psychomotorDevelopment.deleteSuccessMessage', {
            ns: 'patient-pages',
            defaultValue:
              'Psychomotor development assessment deleted successfully',
          }),
        });
      } catch (error) {
        console.error('Error deleting psychomotor development:', error);
        notifications.show({
          color: 'red',
          title: t('psychomotorDevelopment.deleteError', {
            ns: 'patient-pages',
            defaultValue: 'Error',
          }),
          message: t('psychomotorDevelopment.deleteErrorMessage', {
            ns: 'patient-pages',
            defaultValue: 'Failed to delete psychomotor development assessment',
          }),
        });
      }
    }
  };

  const handleSave = async (savedItems: PsychomotorDevelopmentItem[]) => {
    // Save immediately to the server
    await savePsychomotorDevelopment(
      medplum,
      patient,
      encounter,
      savedItems,
      profile as Practitioner | undefined
    );

    // Invalidate cache to force fresh fetches (standard pattern in codebase)
    medplum.invalidateSearches('Observation');
  };

  const handleSaveSuccess = async (
    savedItems: PsychomotorDevelopmentItem[]
  ) => {
    // Reload the data from the server to get the updated observations with IDs
    try {
      const { items: reloadedItems } =
        await loadEncounterPsychomotorDevelopment(medplum, patient, encounter);
      onChange(reloadedItems);

      // Show success notification
      notifications.show({
        color: 'green',
        title: t('psychomotorDevelopment.saveSuccess', {
          ns: 'patient-pages',
          defaultValue: 'Saved',
        }),
        message: t('psychomotorDevelopment.saveSuccessMessage', {
          ns: 'patient-pages',
          defaultValue: 'Psychomotor development assessment saved successfully',
        }),
      });
    } catch (error) {
      console.error('Error reloading psychomotor development:', error);
      // Even if reload fails, update with the saved items
      onChange(savedItems);
    }
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Group gap="xs">
        <Text size="xl" fw={500}>
          {t('psychomotorDevelopment.title', {
            ns: 'patient-pages',
            defaultValue: 'Psychomotor Development',
          })}
        </Text>
        {hasData && (
          <Badge size="sm" variant="light">
            {
              items.filter((item) => item.value !== null || item.observations)
                .length
            }
          </Badge>
        )}
      </Group>

      {/* Main content area with previous encounter on the right */}
      <Group align="stretch" gap="lg" wrap="nowrap">
        {/* Left column - Current assessment or empty state */}
        <Stack gap="md" flex={1}>
          {!hasData ? (
            <>
              <Paper withBorder radius="md" p="xl" bg="gray.0">
                <Text size="sm" c="dimmed" ta="center">
                  {t('psychomotorDevelopment.noAssessment', {
                    ns: 'patient-pages',
                    defaultValue:
                      'No psychomotor development assessment recorded',
                  })}
                </Text>
              </Paper>
              {/* Edit button */}
              {!readonly && (
                <Button
                  leftSection={<IconPlus size={16} />}
                  variant="light"
                  size="sm"
                  onClick={handleAdd}
                >
                  {t('psychomotorDevelopment.addAssessment', {
                    ns: 'patient-pages',
                    defaultValue: 'Add Assessment',
                  })}
                </Button>
              )}
            </>
          ) : (
            <PsychomotorDevelopmentListItem
              items={items}
              onAction={handleAction}
              isDeleting={false}
              readonly={readonly}
            />
          )}
        </Stack>

        {/* Right column - Previous encounter data */}
        {!loadingPrevious && (
          <PsychomotorDevelopmentPrevious
            items={previousData?.items || []}
            encounterDate={previousData?.encounterDate}
          />
        )}
      </Group>

      {/* Modal */}
      <PsychomotorDevelopmentInputModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        onSave={handleSave}
        onSaveSuccess={handleSaveSuccess}
        items={hasData ? items : undefined}
        mode={modalMode}
      />
    </Stack>
  );
};
